import {
  QueryClientProvider,
  QueryClient,
  MutationCache,
  QueryCache,
} from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { AxiosError } from "axios";
import React from "react";
import { useCallback, useState } from "react";
import type { ApiErrorResponse, CustomMeta } from "src/utils/types/react-query";

import AlertStore from "@utils/alert/store";
import "@utils/api/api";

type QueryProviderProps = {
  hideDevTools?: boolean;
  children: React.ReactNode;
};

const QueryProvider = ({ hideDevTools, children }: QueryProviderProps) => {
  // default error handler for mutations
  const handleError = useCallback(
    (error: Error | AxiosError<ApiErrorResponse>, meta: CustomMeta) => {
      const disableGlobalErrorHandler = meta?.disableGlobalErrorHandler;

      const needDisableGlobalError =
        typeof disableGlobalErrorHandler === "function"
          ? disableGlobalErrorHandler(error)
          : disableGlobalErrorHandler;

      // If the global error handler is disabled, we don't show any alert or redirect
      if (needDisableGlobalError) {
        return;
      }

      if (error instanceof AxiosError) {
        if (error.response?.status === 401 || error.response?.status === 403) {
          const pathname = window.location.pathname;
          if (pathname === "/signin") {
            return;
          }
          // redirect to sign in page
          window.location.href = "/signin";
          return;
        }

        AlertStore.addAlert({
          type: "critical",
          message:
            error.response?.data?.msg ||
            error.response?.data?.what ||
            error.response?.data?.message ||
            error.message,
        });
      } else {
        AlertStore.addAlert({
          type: "critical",
          message: error.message,
        });
      }
    },
    [],
  );

  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false,
            retry: false,
            networkMode: "always",
            staleTime: 1000 * 60,
          },
        },
        queryCache: new QueryCache({
          onError: (error, query) => {
            handleError(error, query.meta);
          },
        }),
        mutationCache: new MutationCache({
          onError: (error, variable, context, mutation) => {
            handleError(error, mutation?.meta);
          },
        }),
      }),
  );

  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {!hideDevTools && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
};

export default QueryProvider;
